/**
 * Route tree shared by the client entry (createBrowserRouter in app.tsx) and
 * the build-time prerenderer (createStaticHandler in entry-server.tsx).
 *
 * PUBLIC_ROUTES is derived from this tree and drives which routes get
 * prerendered to static HTML; scripts/prerender.mjs cross-checks it against
 * public/sitemap.xml and fails the build on drift.
 */
import type { RouteObject } from 'react-router';
import { RootLayout } from './components/layout/root-layout';
import { CouncilReportsLayout } from './components/councils/council-reports-layout';
import { HomePage } from './pages/home';
import { AboutPage } from './pages/about';
import { LoginPage } from './pages/auth/login';
import { RegisterPage } from './pages/auth/register';
import { ForgotPasswordPage } from './pages/auth/forgot-password';
import { ResetPasswordPage } from './pages/auth/reset-password';
import { DashboardPage } from './pages/dashboard';
import { ClaimsPage } from './pages/claims';
import { ClaimDetailPage } from './pages/claims/detail';
import { NewClaimPage } from './pages/claims/new';
import { AdminPage } from './pages/admin';
import { ProtectedRoute } from './components/auth/protected-route';
import { AdminRoute } from './components/auth/admin-route';
import { NotFoundPage } from './pages/not-found';
import { PrivacyPage } from './pages/privacy';
import { FAQPage } from './pages/faq';
import { HowItWorksPage } from './pages/how-it-works';
import { PricingPage } from './pages/pricing';
import { CheckPage } from './pages/check';
import { SampleCouncilClaimPackPage } from './pages/sample-council-claim-pack';
import { GuidesPage } from './pages/guides';
import { BlogPage } from './pages/blog';
import { NewYearSamePotholesBlogPost } from './pages/blog/new-year-same-potholes';
import { PotholeClaimEvidenceBlogPost } from './pages/blog/what-evidence-do-you-need-for-pothole-damage-claim';
import { SpringPotholeSeasonBlogPost } from './pages/blog/spring-pothole-season';
import { CouncilRejectedPotholeClaimBlogPost } from './pages/blog/council-rejected-your-pothole-claim';
import { MechanicsViewHiddenPotholeDamageBlogPost } from './pages/blog/mechanics-view-hidden-pothole-damage';
import { SummerRoadTripsPotholeDamageBlogPost } from './pages/blog/summer-road-trips-pothole-damage';
import { WhatMakesAValidClaimGuide } from './pages/guides/what-makes-a-valid-claim';
import { WhoIsResponsibleGuide } from './pages/guides/who-is-responsible';
import { ClaimTimelineGuide } from './pages/guides/claim-timeline';
import { FixMyStreetPotholeReportingGuide } from './pages/guides/fix-my-street-pothole-reporting';
import { PotholeClaimLetterTemplateGuide } from './pages/guides/pothole-claim-letter-template';
import { PhotographingPotholeDamageGuide } from './pages/guides/photographing-pothole-damage';
import { DocumentingVehicleDamageGuide } from './pages/guides/documenting-vehicle-damage';
import { GettingRepairQuotesGuide } from './pages/guides/getting-repair-quotes';
import { Section41ExplainedGuide } from './pages/guides/section-41-explained';
import { Section58DefenceGuide } from './pages/guides/section-58-defence';
import { PriorReportsExplainedGuide } from './pages/guides/prior-reports-explained';
import { UnderstandingRejectionReasonsGuide } from './pages/guides/understanding-rejection-reasons';
import { SmallClaimsCourtGuide } from './pages/guides/small-claims-court-guide';
import { WhatToExpectAtHearingGuide } from './pages/guides/what-to-expect-at-hearing';
import { NationalHighwaysPotholeClaimsGuide } from './pages/guides/national-highways-pothole-claims';
import { EnglandWalesPotholeClaimsGuide } from './pages/guides/england-wales-pothole-claims';
import { RequestRoadInspectionRecordsGuide } from './pages/guides/request-road-inspection-records';
import { CouncilClaimOrCarInsuranceGuide } from './pages/guides/council-claim-or-car-insurance';
import { DashcamPotholeEvidenceGuide } from './pages/guides/dashcam-pothole-evidence';
import { PotholeClaimRejectedNextStepsGuide } from './pages/guides/pothole-claim-rejected-next-steps';
import { HighwayAuthorityClaimRoutesGuide } from './pages/guides/highway-authority-claim-routes';
import { TermsPage } from './pages/terms';
import { CookiesPage } from './pages/cookies';
import { ContactPage } from './pages/contact';
import { AuthorityRatingsPage } from './pages/authority-ratings';
import { RoadMaintenanceReportPage } from './pages/road-maintenance-report';
import { CouncilRoadMaintenanceDatasetPage } from './pages/council-road-maintenance-dataset';
import { DerbyshireCouncilPage } from './pages/councils/derbyshire';
import { DudleyCouncilPage } from './pages/councils/dudley';
import { LeicestershireCouncilPage } from './pages/councils/leicestershire';
import { SloughCouncilPage } from './pages/councils/slough';
import { BoltonCouncilPage } from './pages/councils/bolton';
import { BedfordCouncilPage } from './pages/councils/bedford';
import { GreenwichCouncilPage } from './pages/councils/greenwich';
import { NorthLincolnshireCouncilPage } from './pages/councils/north-lincolnshire';
import { SuffolkCouncilPage } from './pages/councils/suffolk';
import { CumberlandCouncilPage } from './pages/councils/cumberland';
import { WestmorlandAndFurnessCouncilPage } from './pages/councils/westmorland-and-furness';
import { WestNorthamptonshireCouncilPage } from './pages/councils/west-northamptonshire';
import { WalthamForestCouncilPage } from './pages/councils/waltham-forest';
import { DevonCouncilPage } from './pages/councils/devon';
import { BarkingAndDagenhamCouncilPage } from './pages/councils/barking-and-dagenham';
import { BarnetCouncilPage } from './pages/councils/barnet';
import { BarnsleyCouncilPage } from './pages/councils/barnsley';
import { BathAndNorthEastSomersetCouncilPage } from './pages/councils/bath-and-north-east-somerset';
import { BexleyCouncilPage } from './pages/councils/bexley';
import { BirminghamCouncilPage } from './pages/councils/birmingham';
import { BlackburnWithDarwenCouncilPage } from './pages/councils/blackburn-with-darwen';
import { BlackpoolCouncilPage } from './pages/councils/blackpool';
import { BournemouthChristchurchPooleCouncilPage } from './pages/councils/bournemouth-christchurch-and-poole';
import { BracknellForestCouncilPage } from './pages/councils/bracknell-forest';
import { BradfordCouncilPage } from './pages/councils/bradford';
import { BrentCouncilPage } from './pages/councils/brent';
import { BrightonAndHoveCouncilPage } from './pages/councils/brighton-and-hove';
import { BristolCouncilPage } from './pages/councils/bristol';
import { BromleyCouncilPage } from './pages/councils/bromley';
import { BuckinghamshireCouncilPage } from './pages/councils/buckinghamshire';
import { CambridgeshireCouncilPage } from './pages/councils/cambridgeshire';
import { CornwallCouncilPage } from './pages/councils/cornwall';
import { DurhamCouncilPage } from './pages/councils/durham';
import { EssexCouncilPage } from './pages/councils/essex';
import { HertfordshireCouncilPage } from './pages/councils/hertfordshire';
import { KentCouncilPage } from './pages/councils/kent';
import { LancashireCouncilPage } from './pages/councils/lancashire';
import { LincolnshireCouncilPage } from './pages/councils/lincolnshire';
import { NorfolkCouncilPage } from './pages/councils/norfolk';
import { NorthYorkshireCouncilPage } from './pages/councils/north-yorkshire';
import { NottinghamshireCouncilPage } from './pages/councils/nottinghamshire';
import { SomersetCouncilPage } from './pages/councils/somerset';
import { StaffordshireCouncilPage } from './pages/councils/staffordshire';
import { SurreyCouncilPage } from './pages/councils/surrey';
import { CoventryCouncilPage } from './pages/councils/coventry';
import { WolverhamptonCouncilPage } from './pages/councils/wolverhampton';
import { WorcestershireCouncilPage } from './pages/councils/worcestershire';
import { HampshireCouncilPage } from './pages/councils/hampshire';
import { KensingtonAndChelseaCouncilPage } from './pages/councils/kensington-and-chelsea';
import { CentralBedfordshireCouncilPage } from './pages/councils/central-bedfordshire';
import { CheshireEastCouncilPage } from './pages/councils/cheshire-east';
import { CheshireWestAndChesterCouncilPage } from './pages/councils/cheshire-west-and-chester';
import { DorsetCouncilPage } from './pages/councils/dorset';
import { EastRidingOfYorkshireCouncilPage } from './pages/councils/east-riding-of-yorkshire';
import { EastSussexCouncilPage } from './pages/councils/east-sussex';
import { GloucestershireCouncilPage } from './pages/councils/gloucestershire';
import { LeedsCouncilPage } from './pages/councils/leeds';
import { LeicesterCouncilPage } from './pages/councils/leicester';
import { LiverpoolCouncilPage } from './pages/councils/liverpool';
import { ManchesterCouncilPage } from './pages/councils/manchester';
import { NorthNorthamptonshireCouncilPage } from './pages/councils/north-northamptonshire';
import { NorthumberlandCouncilPage } from './pages/councils/northumberland';
import { NottinghamCouncilPage } from './pages/councils/nottingham';
import { OxfordshireCouncilPage } from './pages/councils/oxfordshire';
import { SheffieldCouncilPage } from './pages/councils/sheffield';
import { ShropshireCouncilPage } from './pages/councils/shropshire';
import { WarwickshireCouncilPage } from './pages/councils/warwickshire';
import { WestSussexCouncilPage } from './pages/councils/west-sussex';
import { WiltshireCouncilPage } from './pages/councils/wiltshire';
import { KirkleesCouncilPage } from './pages/councils/kirklees';
import { WakefieldCouncilPage } from './pages/councils/wakefield';
import { CalderdaleCouncilPage } from './pages/councils/calderdale';
import { DoncasterCouncilPage } from './pages/councils/doncaster';
import { RotherhamCouncilPage } from './pages/councils/rotherham';
import { BuryCouncilPage } from './pages/councils/bury';
import { OldhamCouncilPage } from './pages/councils/oldham';
import { RochdaleCouncilPage } from './pages/councils/rochdale';
import { SalfordCouncilPage } from './pages/councils/salford';
import { StockportCouncilPage } from './pages/councils/stockport';
import { TamesideCouncilPage } from './pages/councils/tameside';
import { TraffordCouncilPage } from './pages/councils/trafford';
import { WiganCouncilPage } from './pages/councils/wigan';
import { KnowsleyCouncilPage } from './pages/councils/knowsley';
import { SeftonCouncilPage } from './pages/councils/sefton';
import { StHelensCouncilPage } from './pages/councils/st-helens';
import { WirralCouncilPage } from './pages/councils/wirral';
import { GatesheadCouncilPage } from './pages/councils/gateshead';
import { NewcastleCouncilPage } from './pages/councils/newcastle';
import { SunderlandCouncilPage } from './pages/councils/sunderland';
import { CamdenCouncilPage } from './pages/councils/camden';
import { CroydonCouncilPage } from './pages/councils/croydon';
import { EalingCouncilPage } from './pages/councils/ealing';
import { EnfieldCouncilPage } from './pages/councils/enfield';
import { HackneyCouncilPage } from './pages/councils/hackney';
import { HammersmithAndFulhamCouncilPage } from './pages/councils/hammersmith-and-fulham';
import { HaringeyCouncilPage } from './pages/councils/haringey';
import { HarrowCouncilPage } from './pages/councils/harrow';
import { HaveringCouncilPage } from './pages/councils/havering';
import { HillingdonCouncilPage } from './pages/councils/hillingdon';
import { HounslowCouncilPage } from './pages/councils/hounslow';
import { IslingtonCouncilPage } from './pages/councils/islington';
import { LambethCouncilPage } from './pages/councils/lambeth';
import { LewishamCouncilPage } from './pages/councils/lewisham';
import { MertonCouncilPage } from './pages/councils/merton';
import { NewhamCouncilPage } from './pages/councils/newham';
import { RedbridgeCouncilPage } from './pages/councils/redbridge';
import { RichmondUponThamesCouncilPage } from './pages/councils/richmond-upon-thames';
import { SouthwarkCouncilPage } from './pages/councils/southwark';
import { SuttonCouncilPage } from './pages/councils/sutton';
import { CityOfLondonCouncilPage } from './pages/councils/city-of-london';
import { DarlingtonCouncilPage } from './pages/councils/darlington';
import { DerbyCouncilPage } from './pages/councils/derby';
import { HaltonCouncilPage } from './pages/councils/halton';
import { HartlepoolCouncilPage } from './pages/councils/hartlepool';
import { HerefordshireCouncilPage } from './pages/councils/herefordshire';
import { HullCouncilPage } from './pages/councils/hull';
import { IsleOfWightCouncilPage } from './pages/councils/isle-of-wight';
import { KingstonUponThamesCouncilPage } from './pages/councils/kingston-upon-thames';
import { LutonCouncilPage } from './pages/councils/luton';
import { MedwayCouncilPage } from './pages/councils/medway';
import { MiddlesbroughCouncilPage } from './pages/councils/middlesbrough';
import { NorthTynesideCouncilPage } from './pages/councils/north-tyneside';
import { SandwellCouncilPage } from './pages/councils/sandwell';
import { SolihullCouncilPage } from './pages/councils/solihull';
import { SouthTynesideCouncilPage } from './pages/councils/south-tyneside';
import { TowerHamletsCouncilPage } from './pages/councils/tower-hamlets';
import { WalsallCouncilPage } from './pages/councils/walsall';
import { WandsworthCouncilPage } from './pages/councils/wandsworth';
import { WestminsterCouncilPage } from './pages/councils/westminster';
import { IslesOfScillyCouncilPage } from './pages/councils/isles-of-scilly';
import { MiltonKeynesCouncilPage } from './pages/councils/milton-keynes';
import { NorthEastLincolnshireCouncilPage } from './pages/councils/north-east-lincolnshire';
import { NorthSomersetCouncilPage } from './pages/councils/north-somerset';
import { PeterboroughCouncilPage } from './pages/councils/peterborough';
import { PlymouthCouncilPage } from './pages/councils/plymouth';
import { PortsmouthCouncilPage } from './pages/councils/portsmouth';
import { ReadingCouncilPage } from './pages/councils/reading';
import { RedcarAndClevelandCouncilPage } from './pages/councils/redcar-and-cleveland';
import { RutlandCouncilPage } from './pages/councils/rutland';
import { SouthGloucestershireCouncilPage } from './pages/councils/south-gloucestershire';
import { SouthamptonCouncilPage } from './pages/councils/southampton';
import { SouthendOnSeaCouncilPage } from './pages/councils/southend-on-sea';
import { StocktonOnTeesCouncilPage } from './pages/councils/stockton-on-tees';
import { StokeOnTrentCouncilPage } from './pages/councils/stoke-on-trent';
import { SwindonCouncilPage } from './pages/councils/swindon';
import { TelfordAndWrekinCouncilPage } from './pages/councils/telford-and-wrekin';
import { ThurrockCouncilPage } from './pages/councils/thurrock';
import { TorbayCouncilPage } from './pages/councils/torbay';
import { WarringtonCouncilPage } from './pages/councils/warrington';
import { WestBerkshireCouncilPage } from './pages/councils/west-berkshire';
import { WindsorAndMaidenheadCouncilPage } from './pages/councils/windsor-and-maidenhead';
import { WokinghamCouncilPage } from './pages/councils/wokingham';
import { YorkCouncilPage } from './pages/councils/york';
import { CouncilsHubPage } from './pages/councils/index';
import { DudleyLandingPage } from './pages/lp/dudley';
import { DerbyshireLandingPage } from './pages/lp/derbyshire';
import { LeicestershireLandingPage } from './pages/lp/leicestershire';
import { SuffolkLandingPage } from './pages/lp/suffolk';
import { SloughLandingPage } from './pages/lp/slough';
import { PotholeHallOfShamePage } from './pages/pothole-hall-of-shame';
import { MeetMacPage } from './pages/meet-mac';
import { SupportPage } from './pages/support';
import { MediaEnquiriesPage } from './pages/media-enquiries';
import { PressIndexPage } from './pages/press';
import { PressReleasePage } from './pages/press/release';
import { pressReleases } from './data/press-releases';
import { ROAD_MAINTENANCE_INDEX } from '@fixtyer/content';
import {
  RoadMaintenanceIndexPage,
  RoadMaintenanceIndexRegionPage,
} from './pages/road-maintenance-index';
import { CrawlerAndCitationAccessPage } from './pages/crawler-and-citation-access';
import { INDEXABLE_PUBLIC_PATHS } from './public-route-manifest';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'register',
        element: <RegisterPage />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: 'cookies',
        element: <CookiesPage />,
      },
      {
        path: 'faq',
        element: <FAQPage />,
      },
      {
        path: 'how-it-works',
        element: <HowItWorksPage />,
      },
      {
        path: 'pricing',
        element: <PricingPage />,
      },
      {
        path: 'check',
        element: <CheckPage />,
      },
      {
        path: 'sample-council-claim-pack',
        element: <SampleCouncilClaimPackPage />,
      },
      {
        path: 'blog',
        children: [
          {
            index: true,
            element: <BlogPage />,
          },
          {
            path: 'new-year-same-potholes',
            element: <NewYearSamePotholesBlogPost />,
          },
          {
            path: 'what-evidence-do-you-need-for-pothole-damage-claim',
            element: <PotholeClaimEvidenceBlogPost />,
          },
          {
            path: 'spring-pothole-season',
            element: <SpringPotholeSeasonBlogPost />,
          },
          {
            path: 'council-rejected-your-pothole-claim',
            element: <CouncilRejectedPotholeClaimBlogPost />,
          },
          {
            path: 'mechanics-view-hidden-pothole-damage',
            element: <MechanicsViewHiddenPotholeDamageBlogPost />,
          },
          {
            path: 'summer-road-trips-pothole-damage',
            element: <SummerRoadTripsPotholeDamageBlogPost />,
          },
        ],
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'authority-ratings',
        element: <AuthorityRatingsPage />,
      },
      {
        path: 'road-maintenance-report',
        element: <RoadMaintenanceReportPage />,
      },
      {
        path: 'data/council-road-maintenance-ratings',
        element: <CouncilRoadMaintenanceDatasetPage />,
      },
      {
        path: 'road-maintenance-index',
        children: [
          {
            index: true,
            element: <RoadMaintenanceIndexPage />,
          },
          ...ROAD_MAINTENANCE_INDEX.regions.map((region) => ({
            path: `regions/${region.slug}`,
            element: <RoadMaintenanceIndexRegionPage region={region} />,
          })),
        ],
      },
      {
        path: 'crawler-and-citation-access',
        element: <CrawlerAndCitationAccessPage />,
      },
      {
        path: 'pothole-hall-of-shame',
        element: <PotholeHallOfShamePage />,
      },
      {
        path: 'meet-mac',
        element: <MeetMacPage />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },
      {
        path: 'media-enquiries',
        element: <MediaEnquiriesPage />,
      },
      {
        // Press releases (newsroom). Release child routes are generated from the
        // press-releases content collection, so new releases drop in as content
        // with no edits here. PUBLIC_ROUTES (and prerender) pick them up
        // automatically; remember to add each /press/{slug} to sitemap.xml. The
        // Render API manifest is generated from PUBLIC_ROUTES at deploy time.
        path: 'press',
        children: [
          {
            index: true,
            element: <PressIndexPage />,
          },
          ...pressReleases.map((release) => ({
            path: release.slug,
            element: <PressReleasePage slug={release.slug} />,
          })),
        ],
      },
      {
        path: 'councils',
        element: <CouncilReportsLayout />,
        children: [
          {
            index: true,
            element: <CouncilsHubPage />,
          },
          {
            path: 'derbyshire',
            element: <DerbyshireCouncilPage />,
          },
          {
            path: 'dudley',
            element: <DudleyCouncilPage />,
          },
          {
            path: 'leicestershire',
            element: <LeicestershireCouncilPage />,
          },
          {
            path: 'slough',
            element: <SloughCouncilPage />,
          },
          {
            path: 'bolton',
            element: <BoltonCouncilPage />,
          },
          {
            path: 'bedford',
            element: <BedfordCouncilPage />,
          },
          {
            path: 'greenwich',
            element: <GreenwichCouncilPage />,
          },
          {
            path: 'north-lincolnshire',
            element: <NorthLincolnshireCouncilPage />,
          },
          {
            path: 'suffolk',
            element: <SuffolkCouncilPage />,
          },
          {
            path: 'cumberland',
            element: <CumberlandCouncilPage />,
          },
          {
            path: 'westmorland-and-furness',
            element: <WestmorlandAndFurnessCouncilPage />,
          },
          {
            path: 'west-northamptonshire',
            element: <WestNorthamptonshireCouncilPage />,
          },
          {
            path: 'waltham-forest',
            element: <WalthamForestCouncilPage />,
          },
          {
            path: 'devon',
            element: <DevonCouncilPage />,
          },
          {
            path: 'barking-and-dagenham',
            element: <BarkingAndDagenhamCouncilPage />,
          },
          {
            path: 'barnet',
            element: <BarnetCouncilPage />,
          },
          {
            path: 'barnsley',
            element: <BarnsleyCouncilPage />,
          },
          {
            path: 'bath-and-north-east-somerset',
            element: <BathAndNorthEastSomersetCouncilPage />,
          },
          {
            path: 'bexley',
            element: <BexleyCouncilPage />,
          },
          {
            path: 'birmingham',
            element: <BirminghamCouncilPage />,
          },
          {
            path: 'blackburn-with-darwen',
            element: <BlackburnWithDarwenCouncilPage />,
          },
          {
            path: 'blackpool',
            element: <BlackpoolCouncilPage />,
          },
          {
            path: 'bournemouth-christchurch-and-poole',
            element: <BournemouthChristchurchPooleCouncilPage />,
          },
          {
            path: 'bracknell-forest',
            element: <BracknellForestCouncilPage />,
          },
          {
            path: 'bradford',
            element: <BradfordCouncilPage />,
          },
          {
            path: 'brent',
            element: <BrentCouncilPage />,
          },
          {
            path: 'brighton-and-hove',
            element: <BrightonAndHoveCouncilPage />,
          },
          {
            path: 'bristol',
            element: <BristolCouncilPage />,
          },
          {
            path: 'bromley',
            element: <BromleyCouncilPage />,
          },
          {
            path: 'buckinghamshire',
            element: <BuckinghamshireCouncilPage />,
          },
          {
            path: 'cambridgeshire',
            element: <CambridgeshireCouncilPage />,
          },
          {
            path: 'cornwall',
            element: <CornwallCouncilPage />,
          },
          {
            path: 'durham',
            element: <DurhamCouncilPage />,
          },
          {
            path: 'essex',
            element: <EssexCouncilPage />,
          },
          {
            path: 'hertfordshire',
            element: <HertfordshireCouncilPage />,
          },
          {
            path: 'kent',
            element: <KentCouncilPage />,
          },
          {
            path: 'lancashire',
            element: <LancashireCouncilPage />,
          },
          {
            path: 'lincolnshire',
            element: <LincolnshireCouncilPage />,
          },
          {
            path: 'norfolk',
            element: <NorfolkCouncilPage />,
          },
          {
            path: 'north-yorkshire',
            element: <NorthYorkshireCouncilPage />,
          },
          {
            path: 'nottinghamshire',
            element: <NottinghamshireCouncilPage />,
          },
          {
            path: 'somerset',
            element: <SomersetCouncilPage />,
          },
          {
            path: 'staffordshire',
            element: <StaffordshireCouncilPage />,
          },
          {
            path: 'surrey',
            element: <SurreyCouncilPage />,
          },
          {
            path: 'coventry',
            element: <CoventryCouncilPage />,
          },
          {
            path: 'wolverhampton',
            element: <WolverhamptonCouncilPage />,
          },
          {
            path: 'worcestershire',
            element: <WorcestershireCouncilPage />,
          },
          {
            path: 'hampshire',
            element: <HampshireCouncilPage />,
          },
          {
            path: 'kensington-and-chelsea',
            element: <KensingtonAndChelseaCouncilPage />,
          },
          {
            path: 'central-bedfordshire',
            element: <CentralBedfordshireCouncilPage />,
          },
          {
            path: 'cheshire-east',
            element: <CheshireEastCouncilPage />,
          },
          {
            path: 'cheshire-west-and-chester',
            element: <CheshireWestAndChesterCouncilPage />,
          },
          {
            path: 'dorset',
            element: <DorsetCouncilPage />,
          },
          {
            path: 'east-riding-of-yorkshire',
            element: <EastRidingOfYorkshireCouncilPage />,
          },
          {
            path: 'east-sussex',
            element: <EastSussexCouncilPage />,
          },
          {
            path: 'gloucestershire',
            element: <GloucestershireCouncilPage />,
          },
          {
            path: 'leeds',
            element: <LeedsCouncilPage />,
          },
          {
            path: 'leicester',
            element: <LeicesterCouncilPage />,
          },
          {
            path: 'liverpool',
            element: <LiverpoolCouncilPage />,
          },
          {
            path: 'manchester',
            element: <ManchesterCouncilPage />,
          },
          {
            path: 'north-northamptonshire',
            element: <NorthNorthamptonshireCouncilPage />,
          },
          {
            path: 'northumberland',
            element: <NorthumberlandCouncilPage />,
          },
          {
            path: 'nottingham',
            element: <NottinghamCouncilPage />,
          },
          {
            path: 'oxfordshire',
            element: <OxfordshireCouncilPage />,
          },
          {
            path: 'sheffield',
            element: <SheffieldCouncilPage />,
          },
          {
            path: 'shropshire',
            element: <ShropshireCouncilPage />,
          },
          {
            path: 'warwickshire',
            element: <WarwickshireCouncilPage />,
          },
          {
            path: 'west-sussex',
            element: <WestSussexCouncilPage />,
          },
          {
            path: 'wiltshire',
            element: <WiltshireCouncilPage />,
          },
          {
            path: 'kirklees',
            element: <KirkleesCouncilPage />,
          },
          {
            path: 'wakefield',
            element: <WakefieldCouncilPage />,
          },
          {
            path: 'calderdale',
            element: <CalderdaleCouncilPage />,
          },
          {
            path: 'doncaster',
            element: <DoncasterCouncilPage />,
          },
          {
            path: 'rotherham',
            element: <RotherhamCouncilPage />,
          },
          {
            path: 'bury',
            element: <BuryCouncilPage />,
          },
          {
            path: 'oldham',
            element: <OldhamCouncilPage />,
          },
          {
            path: 'rochdale',
            element: <RochdaleCouncilPage />,
          },
          {
            path: 'salford',
            element: <SalfordCouncilPage />,
          },
          {
            path: 'stockport',
            element: <StockportCouncilPage />,
          },
          {
            path: 'tameside',
            element: <TamesideCouncilPage />,
          },
          {
            path: 'trafford',
            element: <TraffordCouncilPage />,
          },
          {
            path: 'wigan',
            element: <WiganCouncilPage />,
          },
          {
            path: 'knowsley',
            element: <KnowsleyCouncilPage />,
          },
          {
            path: 'sefton',
            element: <SeftonCouncilPage />,
          },
          {
            path: 'st-helens',
            element: <StHelensCouncilPage />,
          },
          {
            path: 'wirral',
            element: <WirralCouncilPage />,
          },
          {
            path: 'gateshead',
            element: <GatesheadCouncilPage />,
          },
          {
            path: 'newcastle',
            element: <NewcastleCouncilPage />,
          },
          {
            path: 'sunderland',
            element: <SunderlandCouncilPage />,
          },
          {
            path: 'camden',
            element: <CamdenCouncilPage />,
          },
          {
            path: 'croydon',
            element: <CroydonCouncilPage />,
          },
          {
            path: 'ealing',
            element: <EalingCouncilPage />,
          },
          {
            path: 'enfield',
            element: <EnfieldCouncilPage />,
          },
          {
            path: 'hackney',
            element: <HackneyCouncilPage />,
          },
          {
            path: 'hammersmith-and-fulham',
            element: <HammersmithAndFulhamCouncilPage />,
          },
          {
            path: 'haringey',
            element: <HaringeyCouncilPage />,
          },
          {
            path: 'harrow',
            element: <HarrowCouncilPage />,
          },
          {
            path: 'havering',
            element: <HaveringCouncilPage />,
          },
          {
            path: 'hillingdon',
            element: <HillingdonCouncilPage />,
          },
          {
            path: 'hounslow',
            element: <HounslowCouncilPage />,
          },
          {
            path: 'islington',
            element: <IslingtonCouncilPage />,
          },
          {
            path: 'lambeth',
            element: <LambethCouncilPage />,
          },
          {
            path: 'lewisham',
            element: <LewishamCouncilPage />,
          },
          {
            path: 'merton',
            element: <MertonCouncilPage />,
          },
          {
            path: 'newham',
            element: <NewhamCouncilPage />,
          },
          {
            path: 'redbridge',
            element: <RedbridgeCouncilPage />,
          },
          {
            path: 'richmond-upon-thames',
            element: <RichmondUponThamesCouncilPage />,
          },
          {
            path: 'southwark',
            element: <SouthwarkCouncilPage />,
          },
          {
            path: 'sutton',
            element: <SuttonCouncilPage />,
          },
          {
            path: 'city-of-london',
            element: <CityOfLondonCouncilPage />,
          },
          {
            path: 'darlington',
            element: <DarlingtonCouncilPage />,
          },
          {
            path: 'derby',
            element: <DerbyCouncilPage />,
          },
          {
            path: 'halton',
            element: <HaltonCouncilPage />,
          },
          {
            path: 'hartlepool',
            element: <HartlepoolCouncilPage />,
          },
          {
            path: 'herefordshire',
            element: <HerefordshireCouncilPage />,
          },
          {
            path: 'hull',
            element: <HullCouncilPage />,
          },
          {
            path: 'isle-of-wight',
            element: <IsleOfWightCouncilPage />,
          },
          {
            path: 'kingston-upon-thames',
            element: <KingstonUponThamesCouncilPage />,
          },
          {
            path: 'luton',
            element: <LutonCouncilPage />,
          },
          {
            path: 'medway',
            element: <MedwayCouncilPage />,
          },
          {
            path: 'middlesbrough',
            element: <MiddlesbroughCouncilPage />,
          },
          {
            path: 'north-tyneside',
            element: <NorthTynesideCouncilPage />,
          },
          {
            path: 'sandwell',
            element: <SandwellCouncilPage />,
          },
          {
            path: 'solihull',
            element: <SolihullCouncilPage />,
          },
          {
            path: 'south-tyneside',
            element: <SouthTynesideCouncilPage />,
          },
          {
            path: 'tower-hamlets',
            element: <TowerHamletsCouncilPage />,
          },
          {
            path: 'walsall',
            element: <WalsallCouncilPage />,
          },
          {
            path: 'wandsworth',
            element: <WandsworthCouncilPage />,
          },
          {
            path: 'westminster',
            element: <WestminsterCouncilPage />,
          },
          {
            path: 'isles-of-scilly',
            element: <IslesOfScillyCouncilPage />,
          },
          {
            path: 'milton-keynes',
            element: <MiltonKeynesCouncilPage />,
          },
          {
            path: 'north-east-lincolnshire',
            element: <NorthEastLincolnshireCouncilPage />,
          },
          {
            path: 'north-somerset',
            element: <NorthSomersetCouncilPage />,
          },
          {
            path: 'peterborough',
            element: <PeterboroughCouncilPage />,
          },
          {
            path: 'plymouth',
            element: <PlymouthCouncilPage />,
          },
          {
            path: 'portsmouth',
            element: <PortsmouthCouncilPage />,
          },
          {
            path: 'reading',
            element: <ReadingCouncilPage />,
          },
          {
            path: 'redcar-and-cleveland',
            element: <RedcarAndClevelandCouncilPage />,
          },
          {
            path: 'rutland',
            element: <RutlandCouncilPage />,
          },
          {
            path: 'south-gloucestershire',
            element: <SouthGloucestershireCouncilPage />,
          },
          {
            path: 'southampton',
            element: <SouthamptonCouncilPage />,
          },
          {
            path: 'southend-on-sea',
            element: <SouthendOnSeaCouncilPage />,
          },
          {
            path: 'stockton-on-tees',
            element: <StocktonOnTeesCouncilPage />,
          },
          {
            path: 'stoke-on-trent',
            element: <StokeOnTrentCouncilPage />,
          },
          {
            path: 'swindon',
            element: <SwindonCouncilPage />,
          },
          {
            path: 'telford-and-wrekin',
            element: <TelfordAndWrekinCouncilPage />,
          },
          {
            path: 'thurrock',
            element: <ThurrockCouncilPage />,
          },
          {
            path: 'torbay',
            element: <TorbayCouncilPage />,
          },
          {
            path: 'warrington',
            element: <WarringtonCouncilPage />,
          },
          {
            path: 'west-berkshire',
            element: <WestBerkshireCouncilPage />,
          },
          {
            path: 'windsor-and-maidenhead',
            element: <WindsorAndMaidenheadCouncilPage />,
          },
          {
            path: 'wokingham',
            element: <WokinghamCouncilPage />,
          },
          {
            path: 'york',
            element: <YorkCouncilPage />,
          },
        ],
      },
      {
        // PPC landing pages — noindex'd, never prerendered (see PRERENDER_EXCLUDE).
        path: 'lp',
        children: [
          {
            path: 'dudley',
            element: <DudleyLandingPage />,
          },
          {
            path: 'derbyshire',
            element: <DerbyshireLandingPage />,
          },
          {
            path: 'leicestershire',
            element: <LeicestershireLandingPage />,
          },
          {
            path: 'suffolk',
            element: <SuffolkLandingPage />,
          },
          {
            path: 'slough',
            element: <SloughLandingPage />,
          },
        ],
      },
      {
        path: 'guides',
        children: [
          {
            index: true,
            element: <GuidesPage />,
          },
          // Getting Started
          {
            path: 'what-makes-a-valid-claim',
            element: <WhatMakesAValidClaimGuide />,
          },
          {
            path: 'who-is-responsible',
            element: <WhoIsResponsibleGuide />,
          },
          {
            path: 'claim-timeline',
            element: <ClaimTimelineGuide />,
          },
          {
            path: 'fix-my-street-pothole-reporting',
            element: <FixMyStreetPotholeReportingGuide />,
          },
          // Evidence & Documentation
          {
            path: 'photographing-pothole-damage',
            element: <PhotographingPotholeDamageGuide />,
          },
          {
            path: 'documenting-vehicle-damage',
            element: <DocumentingVehicleDamageGuide />,
          },
          {
            path: 'getting-repair-quotes',
            element: <GettingRepairQuotesGuide />,
          },
          {
            path: 'pothole-claim-letter-template',
            element: <PotholeClaimLetterTemplateGuide />,
          },
          // Know Your Rights
          {
            path: 'section-41-explained',
            element: <Section41ExplainedGuide />,
          },
          {
            path: 'section-58-defence',
            element: <Section58DefenceGuide />,
          },
          {
            path: 'prior-reports-explained',
            element: <PriorReportsExplainedGuide />,
          },
          // If Your Claim Is Rejected
          {
            path: 'understanding-rejection-reasons',
            element: <UnderstandingRejectionReasonsGuide />,
          },
          {
            path: 'small-claims-court-guide',
            element: <SmallClaimsCourtGuide />,
          },
          {
            path: 'what-to-expect-at-hearing',
            element: <WhatToExpectAtHearingGuide />,
          },
          // High-intent England and Wales task guides
          {
            path: 'national-highways-pothole-claims',
            element: <NationalHighwaysPotholeClaimsGuide />,
          },
          {
            path: 'england-wales-pothole-claims',
            element: <EnglandWalesPotholeClaimsGuide />,
          },
          {
            path: 'request-road-inspection-records',
            element: <RequestRoadInspectionRecordsGuide />,
          },
          {
            path: 'council-claim-or-car-insurance',
            element: <CouncilClaimOrCarInsuranceGuide />,
          },
          {
            path: 'dashcam-pothole-evidence',
            element: <DashcamPotholeEvidenceGuide />,
          },
          {
            path: 'pothole-claim-rejected-next-steps',
            element: <PotholeClaimRejectedNextStepsGuide />,
          },
          {
            path: 'highway-authority-claim-routes',
            element: <HighwayAuthorityClaimRoutesGuide />,
          },
        ],
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        ),
      },
      {
        path: 'claims',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ClaimsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <NewClaimPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ClaimDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];

// Routes that must NOT be prerendered: auth flows, the authenticated app,
// the contact page (noindex'd, Turnstile widget), PPC landing pages
// (noindex'd, kept out of the sitemap), and catch-alls.
const PRERENDER_EXCLUDE = new Set([
  '/check',
  '/lp/dudley',
  '/lp/derbyshire',
  '/lp/leicestershire',
  '/lp/suffolk',
  '/lp/slough',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/contact',
  '/dashboard',
  '/admin',
  '/claims',
  '/claims/new',
]);

function collectStaticPaths(routeList: RouteObject[], base: string): string[] {
  const paths: string[] = [];
  for (const route of routeList) {
    let current = base;
    if (route.index) {
      paths.push(base === '' ? '/' : base);
    } else if (route.path) {
      // Skip dynamic segments and catch-alls
      if (route.path.includes(':') || route.path.includes('*')) continue;
      current = route.path.startsWith('/')
        ? route.path
        : `${base === '/' ? '' : base}/${route.path}`;
      if (route.element && !route.children) {
        paths.push(current === '' ? '/' : current);
      }
    }
    if (route.children) {
      paths.push(...collectStaticPaths(route.children, route.path === '/' ? '' : current));
    }
  }
  return paths;
}

/** Static, indexable routes prerendered at build time (mirrors sitemap.xml). */
export const ROUTE_TREE_PUBLIC_ROUTES: string[] = collectStaticPaths(routes, '').filter(
  (path) => !PRERENDER_EXCLUDE.has(path)
);
export const PUBLIC_ROUTES: string[] = [...INDEXABLE_PUBLIC_PATHS];
